import React, { useState } from 'react';

import Carousel, { Slide } from '../../library/carousel/carousel';
import Image from '../../library/image/image';
import ProductImageGalleryModal from './components/product-image-gallery-modal/product-image-gallery-modal';

import { useMediaQuery } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import {
  Maybe,
  ProductData,
  SwatchImageData
} from '../../shared/types/graphql-types';
import { PRODUCT_IMAGE_FORMATS } from '../../shared/constants/product-images';

import './product-image-gallery.scss';

export interface ImageInterface {
  altText: string;
  secondaryAltText?: string;
  secondaryUrl?: string;
  url: string;
}

export interface GalleryImages {
  product?: ImageInterface[];
  thumbnail?: ImageInterface[];
  zoom?: ImageInterface[];
}

interface ProductImageGalleryProps {
  fallbackProductImage: string;
  galleryImages: GalleryImages;
  height?: string;
  product?: Maybe<ProductData>;
  selectedSwatch?: SwatchImageData;
  showFallbackProductImage?: boolean;
  width?: string;
}

function ProductImageGallery(props: ProductImageGalleryProps) {
  const {
    fallbackProductImage,
    galleryImages,
    height = '300',
    product,
    selectedSwatch,
    showFallbackProductImage,
    width = '300'
  } = props;

  const [imageIdx, setImageIdx] = useState(0);
  const [modalImage, setModalImage] = useState<ImageInterface | null>(null);
  const [show360View, setShow360View] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const breakpoint = useMediaQuery();

  const productCode =
    selectedSwatch?.productCode ??
    product?.aggregates?.codes?.[0] ??
    product?.code;

  //TODO: Use dot notation once this is available in all environments
  const is360ViewAvailable = product?.['is360ViewAvailable'];

  let productImages;
  if (process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ === 'true') {
    productImages = Array.isArray(
      galleryImages?.[PRODUCT_IMAGE_FORMATS.PRODUCT_MEDIUM]
    )
      ? [...galleryImages[PRODUCT_IMAGE_FORMATS.PRODUCT_MEDIUM]]
      : [];
  } else {
    productImages = Array.isArray(galleryImages?.product)
      ? [...galleryImages.product]
      : [];
  }

  let thumbnailImages;
  if (process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ === 'true') {
    thumbnailImages = Array.isArray(
      galleryImages?.[PRODUCT_IMAGE_FORMATS.PRODUCT_X_SMALL]
    )
      ? [...galleryImages[PRODUCT_IMAGE_FORMATS.PRODUCT_X_SMALL]]
      : [];
  } else {
    thumbnailImages = Array.isArray(galleryImages?.thumbnail)
      ? [...galleryImages.thumbnail]
      : [];
  }

  if (
    process.env.__FF_PDP_360_WHEEL_VIEW__ === 'true' &&
    is360ViewAvailable &&
    productImages.length > 0 &&
    thumbnailImages.length > 0
  ) {
    productImages.push(productImages[0]);
    thumbnailImages.push(thumbnailImages[0]);
  }

  function openImageModal(imageIdx) {
    let zoomImage: ImageInterface | null;
    if (process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ === 'true') {
      zoomImage = Array.isArray(
        galleryImages?.[PRODUCT_IMAGE_FORMATS.PRODUCT_LARGE]
      )
        ? galleryImages[PRODUCT_IMAGE_FORMATS.PRODUCT_LARGE][imageIdx]
        : null;
    } else {
      zoomImage = Array.isArray(galleryImages?.zoom)
        ? galleryImages.zoom[imageIdx]
        : null;
    }

    if (zoomImage) {
      setModalImage(zoomImage);
      setIsImageModalOpen(true);
    } else if (
      process.env.__FF_PDP_360_WHEEL_VIEW__ === 'true' &&
      is360ViewAvailable
    ) {
      setIsImageModalOpen(true);
      setShow360View(true);
      trackAction('product_wheels_360_degree_click');
    }
  }

  function closeImageModal() {
    setIsImageModalOpen(false);
    setModalImage(null);
    setShow360View(false);
  }

  return (
    <div
      className="product-image-gallery"
      styleName={
        showFallbackProductImage
          ? 'product-fallback-container'
          : 'product-carousel-container'
      }
    >
      {isArrayWithLength(productImages) && (
        <div
          className="product-image-gallery__product-carousel"
          styleName="product-carousel"
        >
          <Carousel
            hasFullWidthSlides
            onSlideChange={setImageIdx}
            showIndicators={breakpoint.isXS}
            slideIndex={imageIdx}
          >
            {productImages.map((image, idx) => (
              <Slide key={`gi-${idx}`} shouldLazyLoad>
                <button
                  onClick={() => {
                    openImageModal(idx);
                  }}
                  styleName="product-carousel-slide"
                >
                  <Image
                    alt={image.altText}
                    className="product-image-gallery__product-carousel-image"
                    height={height}
                    src={image.url}
                    title={image.altText}
                    width={width}
                  />

                  {process.env.__FF_PDP_360_WHEEL_VIEW__ === 'true' &&
                    is360ViewAvailable &&
                    idx === productImages.length - 1 && (
                      <img
                        alt="360 degree icon"
                        src="https://discounttire.scene7.com/is/content/discounttire/logos/icons/360-view-icon.svg"
                        styleName="slide-360-icon"
                      />
                    )}
                </button>
              </Slide>
            ))}
          </Carousel>
        </div>
      )}

      {showFallbackProductImage && (
        <div
          style={{
            backgroundImage: `url(${fallbackProductImage})`,
            height: '190px'
          }}
          styleName="product-fallback-image"
        />
      )}

      {isArrayWithLength(thumbnailImages) && breakpoint.isMinSm && (
        <div
          className="product-image-gallery__product-carousel-thumbnails"
          styleName="product-carousel-thumbnails"
        >
          {thumbnailImages.map((img, idx) => (
            <button
              className={
                idx === imageIdx
                  ? 'product-image-gallery__product-carousel-thumbnail-selected'
                  : 'product-image-gallery__product-carousel-thumbnail'
              }
              key={`thumbnail-${idx}`}
              onClick={() => setImageIdx(idx)}
              styleName={
                idx === imageIdx
                  ? 'product-carousel-thumbnail-selected'
                  : 'product-carousel-thumbnail'
              }
            >
              <img
                alt={img.altText}
                height="68"
                src={img.url}
                title={img.altText}
                width="68"
              />

              {process.env.__FF_PDP_360_WHEEL_VIEW__ === 'true' &&
                is360ViewAvailable &&
                idx === thumbnailImages.length - 1 && (
                  <div styleName="thumbnail-360-icon">
                    <img
                      alt="360 degree icon"
                      src="https://discounttire.scene7.com/is/content/discounttire/logos/icons/360-view-icon.svg"
                    />
                  </div>
                )}
            </button>
          ))}
        </div>
      )}

      {isImageModalOpen && (
        <ProductImageGalleryModal
          image={modalImage}
          isOpen
          onClose={closeImageModal}
          productCode={productCode}
          show360View={show360View}
        />
      )}
    </div>
  );
}

export default ProductImageGallery;
